/**
 * A single payslip, laid out for printing.
 * Route: /payslips/:id
 * @file src/web/features/payslips/pages/[id].tsx
 *
 * @llm-rule WHEN: Someone downloads or prints one payslip
 * @llm-rule NOTE: "Download" is the browser's own print-to-PDF. That keeps the
 *   app out of the PDF-generation business and gives the user a file that looks
 *   the same everywhere, at the cost of one extra dialog.
 * @llm-rule AVOID: Rendering anything the server did not return — the read is
 *   permission-checked there, so this page must not reconstruct figures.
 */

import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Button, Skeleton, toast } from '@bloomneo/uikit';
import { Printer } from 'lucide-react';
import { AuthGuard } from '../../auth';
import { useAppRequest } from '../../../shared/hooks/useAppApi';

interface PayslipDetail {
  id: string;
  month: string;
  grossMinor: number;
  gstMinor: number;
  pfMinor: number;
  netMinor: number;
  currency: string;
  user: { id: string; name: string | null; email: string; title: string | null };
  organisation: { id: string; name: string };
}

function money(minor: number, currency: string): string {
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: currency || 'INR',
    maximumFractionDigits: 2,
  }).format(minor / 100);
}

function monthLabel(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

function Row({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
  return (
    <div
      className={`flex items-baseline justify-between gap-4 py-2 ${
        strong ? 'border-t-2 border-ink/20 font-semibold text-ink' : 'border-t border-line text-ink-soft'
      }`}
    >
      <span className="text-[13px]">{label}</span>
      <span className="tabular-nums text-[13.5px]">{value}</span>
    </div>
  );
}

function PayslipDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { request } = useAppRequest();
  const [slip, setSlip] = useState<PayslipDetail | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!id) return;
    void request<{ payslip: PayslipDetail }>(`/api/payslip/${id}`).then((r) => {
      if (r.ok) setSlip(r.data.payslip);
      else toast.error(r.error);
      setLoading(false);
    });
  }, [id, request]);

  if (loading) return <Skeleton className="m-8 h-72" />;
  if (!slip) {
    return (
      <div className="p-8 text-center text-[13.5px] text-ink-soft">
        That payslip is not available to you.
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-[640px] p-6 print:p-0">
      {/* Hidden when printing — the page itself is the document. */}
      <div className="mb-4 flex justify-end print:hidden">
        <Button size="sm" onClick={() => window.print()}>
          <Printer className="mr-1.5 size-3.5" />
          Download / print
        </Button>
      </div>

      <div className="rounded-[14px] border border-line bg-paper p-6 print:border-0 print:p-0">
        <div className="flex items-baseline justify-between gap-4">
          <div>
            <div className="text-[17px] font-semibold text-ink">{slip.organisation.name}</div>
            <div className="text-[12px] uppercase tracking-[0.12em] text-ink-ghost">Payslip</div>
          </div>
          <div className="text-right text-[13px] text-ink-soft">{monthLabel(slip.month)}</div>
        </div>

        <div className="mt-5 border-t border-line pt-4">
          <div className="text-[14px] font-medium text-ink">{slip.user.name ?? slip.user.email}</div>
          <div className="text-[12.5px] text-ink-soft">{slip.user.title}</div>
          <div className="text-[12.5px] text-ink-ghost">{slip.user.email}</div>
        </div>

        <div className="mt-5">
          <Row label="Gross pay" value={money(slip.grossMinor, slip.currency)} />
          {slip.gstMinor > 0 && <Row label="GST" value={`+ ${money(slip.gstMinor, slip.currency)}`} />}
          {slip.pfMinor > 0 && (
            <Row label="Provident fund" value={`− ${money(slip.pfMinor, slip.currency)}`} />
          )}
          <Row label="Net pay" value={money(slip.netMinor, slip.currency)} strong />
        </div>

        <p className="mt-5 text-[11px] leading-relaxed text-ink-ghost">
          Computer-generated payslip — no signature required. Figures are calculated to the paisa.
        </p>
      </div>
    </div>
  );
}

export default function PayslipDetailRoute() {
  return (
    <AuthGuard>
      <PayslipDetailPage />
    </AuthGuard>
  );
}
